"use client"
import { useState, useEffect } from "react"
import { motion } from "framer-motion"
import {
  Shield,
  Database,
  Share2,
  Cookie,
  Lock,
  UserCheck,
  ExternalLink,
  Baby,
  RefreshCw,
  Mail,
  Eye,
  ChevronRight,
  Clock,
  CheckCircle,
  Users,
  Settings,
} from "lucide-react"
import Header from "../components/Header"
import { Link } from "react-router-dom"
import Footer from "../components/Footer"

export const PrivacyPolicy = () => {
  const pageBnrImg = {
    position: "relative" ,
    backgroundImage: `url('./media/frontend/img/about/blog/1920x700/banner-2.jpg')`,
    backgroundPosition: "center",
    backgroundSize: "cover",
  }

  const [activeSection, setActiveSection] = useState("")
  const [readingProgress, setReadingProgress] = useState(0)

  const sections = [
    { id: "information", title: "Information We Collect", icon: Database },
    { id: "usage", title: "How We Use Your Information", icon: Settings },
    { id: "sharing", title: "Sharing Your Information", icon: Share2 },
    { id: "cookies", title: "Cookies and Tracking Technologies", icon: Cookie },
    { id: "security", title: "Data Security", icon: Lock },
    { id: "rights", title: "Your Rights", icon: UserCheck },
    { id: "links", title: "Third-Party Links", icon: ExternalLink },
    { id: "children", title: "Children's Privacy", icon: Baby },
    { id: "updates", title: "Updates to This Policy", icon: RefreshCw },
    { id: "contact", title: "Contact Us", icon: Mail },
  ]

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY
      const docHeight = document.documentElement.scrollHeight - window.innerHeight
      const progress = (scrollTop / docHeight) * 100
      setReadingProgress(progress)

      // Update active section based on scroll position
      const sectionElements = sections.map((section) => ({
        id: section.id,
        element: document.getElementById(section.id),
      }))

      const currentSection = sectionElements.find((section) => {
        if (section.element) {
          const rect = section.element.getBoundingClientRect()
          return rect.top <= 100 && rect.bottom >= 100
        }
        return false
      })

      if (currentSection) {
        setActiveSection(currentSection.id)
      }
    }

    window.addEventListener("scroll", handleScroll)
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId)
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" })
    }
  }

  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.8, ease: [0.6, -0.05, 0.01, 0.99] },
  }

  const fadeInLeft = {
    initial: { opacity: 0, x: -80 },
    animate: { opacity: 1, x: 0 },
    transition: { duration: 0.8, ease: [0.6, -0.05, 0.01, 0.99] },
  }

  const scaleIn = {
    initial: { opacity: 0, scale: 0.8 },
    animate: { opacity: 1, scale: 1 },
    transition: { duration: 0.6, ease: "easeOut" },
  }

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.1,
      },
    },
  }

  return (
    <>
      <Header />

      {/* Reading Progress Bar */}
      <motion.div
        className="fixed top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary to-primary/80 z-50 origin-left"
        style={{ scaleX: readingProgress / 100 }}
        initial={{ scaleX: 0 }}
        animate={{ scaleX: readingProgress / 100 }}
        transition={{ duration: 0.1 }}
      />

      {/* Enhanced Hero Section */}
      <section className="relative overflow-hidden">
        <div className="pageBanr py-32 relative" style={pageBnrImg}>
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/80"></div>
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/20 to-black/70"></div>

          {/* Animated background elements */}
          <div className="absolute inset-0 overflow-hidden">
            <motion.div
              className="absolute top-20 left-10 w-2 h-2 bg-primary rounded-full"
              animate={{ scale: [1, 1.5, 1], opacity: [0.5, 1, 0.5] }}
              transition={{ duration: 2, repeat: Number.POSITIVE_INFINITY }}
            />
            <motion.div
              className="absolute top-40 right-20 w-3 h-3 bg-white rounded-full"
              animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.8, 0.3] }}
              transition={{ duration: 3, repeat: Number.POSITIVE_INFINITY, delay: 1 }}
            />
            <motion.div
              className="absolute bottom-32 left-1/4 w-1 h-1 bg-primary rounded-full"
              animate={{ scale: [1, 2, 1], opacity: [0.4, 1, 0.4] }}
              transition={{ duration: 2.5, repeat: Number.POSITIVE_INFINITY, delay: 0.5 }}
            />
          </div>

          <motion.div
            className="container mx-auto relative z-[1] text-center px-4"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.3 }}
          >
            <motion.div
              className="inline-block mb-6"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.8, delay: 0.5, type: "spring" }}
            >
              <div className="w-20 h-20 bg-gradient-to-br from-primary/20 to-primary/40 rounded-full flex items-center justify-center backdrop-blur-sm border border-white/20">
                <Shield className="w-10 h-10 text-primary" />
              </div>
            </motion.div>

            <motion.h2
              className="font-bold text-5xl md:text-7xl text-white uppercase tracking-wider mb-6 bg-gradient-to-r from-white via-gray-100 to-white bg-clip-text"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              Privacy Policy
            </motion.h2>

            <motion.div
              className="w-32 h-1 bg-gradient-to-r from-transparent via-primary to-transparent mx-auto mb-8"
              initial={{ width: 0 }}
              animate={{ width: 128 }}
              transition={{ duration: 1, delay: 0.8 }}
            />

            <motion.ul
              className="inline-flex items-center space-x-2 text-white mb-0 bg-black/20 backdrop-blur-sm rounded-full px-6 py-3 border border-white/10"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 1 }}
            >
              <li className="text-lg">
                <Link
                  to="/"
                  className="!text-white hover:!text-primary transition-all duration-300 hover:scale-110 inline-block"
                >
                  Home
                </Link>
              </li>
              <li className="text-white/60">/</li>
              <li className="text-lg font-semibold text-primary">Privacy Policy</li>
            </motion.ul>

            <motion.p
              className="text-xl text-white/90 mt-6 max-w-2xl mx-auto"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 1.2 }}
            >
              Your privacy is important to us. Learn how we protect your data.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Enhanced Privacy Policy Content */}
      <section className="privacyPolicy py-24 bg-gradient-to-br from-gray-50 via-white to-gray-50 relative overflow-hidden">
        {/* Background decorations */}
        <div className="absolute top-0 left-0 w-full h-full">
          <div className="absolute top-20 left-10 w-64 h-64 bg-gradient-to-br from-primary/5 to-transparent rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-10 w-80 h-80 bg-gradient-to-br from-primary/3 to-transparent rounded-full blur-3xl" />
        </div>

        <div className="container mx-auto lg:px-2 px-4 relative z-10">
          <div className="flex lg:flex-row flex-col gap-12">
            {/* Table of Contents - Sticky Sidebar */}
            <motion.div
              className="lg:w-1/4 w-full"
              initial="initial"
              whileInView="animate"
              viewport={{ once: true }}
              variants={fadeInLeft}
            >
              <div className="lg:sticky lg:top-24">
                <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-xl border border-white/50">
                  <div className="flex items-center space-x-3 mb-6">
                    <div className="w-10 h-10 bg-gradient-to-br from-primary/10 to-primary/20 rounded-xl flex items-center justify-center">
                      <Eye className="w-5 h-5 text-primary" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-800">Contents</h3>
                  </div>

                  <nav className="space-y-2">
                    {sections.map((section, index) => {
                      const IconComponent = section.icon
                      return (
                        <motion.button
                          key={section.id}
                          onClick={() => scrollToSection(section.id)}
                          className={`w-full flex items-center space-x-3 p-3 rounded-xl text-left transition-all duration-300 group ${
                            activeSection === section.id
                              ? "bg-gradient-to-r from-primary/10 to-primary/5 text-primary border border-primary/20"
                              : "hover:bg-gray-50 text-gray-700 hover:text-primary"
                          }`}
                          whileHover={{ x: 5 }}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ duration: 0.3, delay: index * 0.05 }}
                        >
                          <IconComponent className="w-4 h-4 flex-shrink-0" />
                          <span className="text-sm font-medium flex-1">{section.title}</span>
                          <ChevronRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                        </motion.button>
                      )
                    })}
                  </nav>
                </div>
              </div>
            </motion.div>

            {/* Main Content */}
            <motion.div
              className="lg:w-3/4 w-full"
              initial="initial"
              whileInView="animate"
              viewport={{ once: true }}
              variants={staggerContainer}
            >
              {/* Introduction */}
              <motion.div className="mb-12" variants={fadeInUp}>
                <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-xl border border-white/50">
                  <div className="flex items-center space-x-4 mb-6">
                    <div className="w-12 h-12 bg-gradient-to-br from-primary/10 to-primary/20 rounded-xl flex items-center justify-center">
                      <Shield className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h1 className="text-3xl font-bold text-primary uppercase">Privacy Policy</h1>
                      <div className="flex items-center space-x-2 mt-2">
                        <Clock className="w-4 h-4 text-gray-500" />
                        <p className="text-base text-gray-700">Effective Date: 01 Jan 2025</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-gradient-to-r from-primary/5 to-transparent rounded-2xl p-6 border-l-4 border-primary">
                    <p className="text-lg text-gray-700 leading-relaxed">
                      Welcome to our website. At Shefs Cloud, we are committed to protecting your privacy and ensuring
                      the security of your personal information. This Privacy Policy outlines how we collect, use, and
                      share your information when you visit or interact with our Website.
                    </p>
                  </div>
                </div>
              </motion.div>

              {/* Section 1: Information We Collect */}
              <motion.div id="information" className="mb-12" variants={fadeInUp}>
                <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-xl border border-white/50 hover:shadow-2xl transition-shadow duration-500">
                  <div className="flex items-center space-x-4 mb-6">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500/10 to-blue-600/20 rounded-xl flex items-center justify-center">
                      <Database className="w-6 h-6 text-blue-600" />
                    </div>
                    <h2 className="text-2xl font-bold text-primary uppercase">1. Information We Collect</h2>
                  </div>
                  <div className="space-y-6">
                    <div className="bg-gradient-to-r from-blue-50/50 to-transparent rounded-2xl p-6">
                      <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center space-x-2">
                        <span className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 text-sm font-bold">
                          a
                        </span>
                        <span>Personal Information</span>
                      </h3>
                      <ul className="space-y-3 ml-8">
                        <li className="flex items-start space-x-3">
                          <CheckCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                          <span className="text-lg text-gray-700">
                            Name, email address, phone number, delivery address, payment details (if provided).
                          </span>
                        </li>
                      </ul>
                    </div>
                    <div className="bg-gradient-to-r from-blue-50/50 to-transparent rounded-2xl p-6">
                      <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center space-x-2">
                        <span className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 text-sm font-bold">
                          b
                        </span>
                        <span>Non-Personal Information</span>
                      </h3>
                      <ul className="space-y-3 ml-8">
                        <li className="flex items-start space-x-3">
                          <CheckCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                          <span className="text-lg text-gray-700">
                            Browser type, IP address, device type, operating system, and browsing activity on our
                            Website.
                          </span>
                        </li>
                      </ul>
                    </div>
                    <div className="bg-gradient-to-r from-blue-50/50 to-transparent rounded-2xl p-6">
                      <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center space-x-2">
                        <span className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 text-sm font-bold">
                          c
                        </span>
                        <span>User-Generated Content</span>
                      </h3>
                      <ul className="space-y-3 ml-8">
                        <li className="flex items-start space-x-3">
                          <CheckCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                          <span className="text-lg text-gray-700">
                            Reviews, ratings, or comments submitted on the Website.
                          </span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Section 2: How We Use Your Information */}
              <motion.div id="usage" className="mb-12" variants={fadeInUp}>
                <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-xl border border-white/50 hover:shadow-2xl transition-shadow duration-500">
                  <div className="flex items-center space-x-4 mb-6">
                    <div className="w-12 h-12 bg-gradient-to-br from-green-500/10 to-green-600/20 rounded-xl flex items-center justify-center">
                      <Settings className="w-6 h-6 text-green-600" />
                    </div>
                    <h2 className="text-2xl font-bold text-primary uppercase">2. How We Use Your Information</h2>
                  </div>
                  <div className="bg-gradient-to-r from-green-50/50 to-transparent rounded-2xl p-6">
                    <ul className="space-y-4">
                      <li className="flex items-start space-x-3">
                        <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                        <span className="text-lg text-gray-700">Process and deliver your food orders.</span>
                      </li>
                      <li className="flex items-start space-x-3">
                        <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                        <span className="text-lg text-gray-700">Process and deliver your food orders.</span>
                      </li>
                      <li className="flex items-start space-x-3">
                        <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                        <span className="text-lg text-gray-700">Provide customer support and resolve disputes.</span>
                      </li>
                      <li className="flex items-start space-x-3">
                        <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                        <span className="text-lg text-gray-700">Improve our Website, products, and services.</span>
                      </li>
                      <li className="flex items-start space-x-3">
                        <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                        <span className="text-lg text-gray-700">
                          Send promotional materials and updates (with your consent).
                        </span>
                      </li>
                      <li className="flex items-start space-x-3">
                        <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                        <span className="text-lg text-gray-700">Analyze usage trends to enhance user experience.</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </motion.div>

              {/* Section 3: Sharing Your Information */}
              <motion.div id="sharing" className="mb-12" variants={fadeInUp}>
                <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-xl border border-white/50 hover:shadow-2xl transition-shadow duration-500">
                  <div className="flex items-center space-x-4 mb-6">
                    <div className="w-12 h-12 bg-gradient-to-br from-purple-500/10 to-purple-600/20 rounded-xl flex items-center justify-center">
                      <Share2 className="w-6 h-6 text-purple-600" />
                    </div>
                    <h2 className="text-2xl font-bold text-primary uppercase">3. Sharing Your Information</h2>
                  </div>
                  <div className="bg-gradient-to-r from-purple-50/50 to-transparent rounded-2xl p-6">
                    <p className="text-lg text-gray-700 mb-6">
                      We do not sell your personal information. However, we may share your information with:
                    </p>
                    <ul className="space-y-4">
                      <li className="flex items-start space-x-3">
                        <Users className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
                        <span className="text-lg text-gray-700">
                          <strong>Service Providers:</strong> Delivery partners, payment gateways, and IT support
                          providers.
                        </span>
                      </li>
                      <li className="flex items-start space-x-3">
                        <Shield className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
                        <span className="text-lg text-gray-700">
                          <strong>Legal Authorities:</strong> If required by law or to protect our legal rights.
                        </span>
                      </li>
                      <li className="flex items-start space-x-3">
                        <Users className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
                        <span className="text-lg text-gray-700">
                          <strong>Business Partners:</strong> To offer promotions or joint services (only with your
                          consent).
                        </span>
                      </li>
                    </ul>
                  </div>
                </div>
              </motion.div>

              {/* Section 4: Cookies and Tracking Technologies */}
              <motion.div id="cookies" className="mb-12" variants={fadeInUp}>
                <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-xl border border-white/50 hover:shadow-2xl transition-shadow duration-500">
                  <div className="flex items-center space-x-4 mb-6">
                    <div className="w-12 h-12 bg-gradient-to-br from-orange-500/10 to-orange-600/20 rounded-xl flex items-center justify-center">
                      <Cookie className="w-6 h-6 text-orange-600" />
                    </div>
                    <h2 className="text-2xl font-bold text-primary uppercase">4. Cookies and Tracking Technologies</h2>
                  </div>
                  <div className="bg-gradient-to-r from-orange-50/50 to-transparent rounded-2xl p-6">
                    <p className="text-lg text-gray-700 mb-6">We use cookies and similar technologies to:</p>
                    <ul className="space-y-4 mb-6">
                      <li className="flex items-start space-x-3">
                        <Cookie className="w-5 h-5 text-orange-600 mt-0.5 flex-shrink-0" />
                        <span className="text-lg text-gray-700">Remember your preferences.</span>
                      </li>
                      <li className="flex items-start space-x-3">
                        <Cookie className="w-5 h-5 text-orange-600 mt-0.5 flex-shrink-0" />
                        <span className="text-lg text-gray-700">Track Website usage for analytics purposes.</span>
                      </li>
                      <li className="flex items-start space-x-3">
                        <Cookie className="w-5 h-5 text-orange-600 mt-0.5 flex-shrink-0" />
                        <span className="text-lg text-gray-700">Deliver targeted advertisements.</span>
                      </li>
                    </ul>
                    <div className="bg-orange-100/50 rounded-xl p-4 border-l-4 border-orange-500">
                      <p className="text-lg text-gray-700">
                        You can manage cookie preferences through your browser settings.
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Section 5: Data Security */}
              <motion.div id="security" className="mb-12" variants={fadeInUp}>
                <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-xl border border-white/50 hover:shadow-2xl transition-shadow duration-500">
                  <div className="flex items-center space-x-4 mb-6">
                    <div className="w-12 h-12 bg-gradient-to-br from-emerald-500/10 to-emerald-600/20 rounded-xl flex items-center justify-center">
                      <Lock className="w-6 h-6 text-emerald-600" />
                    </div>
                    <h2 className="text-2xl font-bold text-primary uppercase">5. Data Security</h2>
                  </div>
                  <div className="bg-gradient-to-r from-emerald-50/50 to-transparent rounded-2xl p-6">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Lock className="w-6 h-6 text-emerald-600" />
                      </div>
                      <p className="text-lg text-gray-700 leading-relaxed">
                        We implement appropriate technical and organizational measures to protect your personal
                        information from unauthorized access, loss, or misuse.
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Section 6: Your Rights */}
              <motion.div id="rights" className="mb-12" variants={fadeInUp}>
                <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-xl border border-white/50 hover:shadow-2xl transition-shadow duration-500">
                  <div className="flex items-center space-x-4 mb-6">
                    <div className="w-12 h-12 bg-gradient-to-br from-red-500/10 to-red-600/20 rounded-xl flex items-center justify-center">
                      <UserCheck className="w-6 h-6 text-red-600" />
                    </div>
                    <h2 className="text-2xl font-bold text-primary uppercase">6. Your Rights</h2>
                  </div>
                  <div className="bg-gradient-to-r from-red-50/50 to-transparent rounded-2xl p-6">
                    <p className="text-lg text-gray-700 mb-6">
                      Depending on your jurisdiction, you may have the following rights:
                    </p>
                    <ul className="space-y-4 mb-6">
                      <li className="flex items-start space-x-3">
                        <UserCheck className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                        <span className="text-lg text-gray-700">Access and update your personal information.</span>
                      </li>
                      <li className="flex items-start space-x-3">
                        <UserCheck className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                        <span className="text-lg text-gray-700">Request deletion of your data.</span>
                      </li>
                      <li className="flex items-start space-x-3">
                        <UserCheck className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                        <span className="text-lg text-gray-700">Opt-out of marketing communications.</span>
                      </li>
                      <li className="flex items-start space-x-3">
                        <UserCheck className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                        <span className="text-lg text-gray-700">
                          File a complaint with a data protection authority.
                        </span>
                      </li>
                    </ul>
                    <div className="bg-red-100/50 rounded-xl p-4 border-l-4 border-red-500">
                      <p className="text-lg text-gray-700">
                        To exercise these rights, please contact us at{" "}
                        <a
                          href="mailto:info@shefscloud.com"
                          className="text-primary hover:text-primary/80 underline transition-colors duration-300"
                        >
                          info@shefscloud.com
                        </a>
                        .
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Section 7: Third-Party Links */}
              <motion.div id="links" className="mb-12" variants={fadeInUp}>
                <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-xl border border-white/50 hover:shadow-2xl transition-shadow duration-500">
                  <div className="flex items-center space-x-4 mb-6">
                    <div className="w-12 h-12 bg-gradient-to-br from-yellow-500/10 to-yellow-600/20 rounded-xl flex items-center justify-center">
                      <ExternalLink className="w-6 h-6 text-yellow-600" />
                    </div>
                    <h2 className="text-2xl font-bold text-primary uppercase">7. Third-Party Links</h2>
                  </div>
                  <div className="bg-gradient-to-r from-yellow-50/50 to-transparent rounded-2xl p-6">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center flex-shrink-0">
                        <ExternalLink className="w-6 h-6 text-yellow-600" />
                      </div>
                      <p className="text-lg text-gray-700 leading-relaxed">
                        Our Website may contain links to third-party websites. We are not responsible for their privacy
                        practices, so please review their policies.
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Section 8: Children's Privacy */}
              <motion.div id="children" className="mb-12" variants={fadeInUp}>
                <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-xl border border-white/50 hover:shadow-2xl transition-shadow duration-500">
                  <div className="flex items-center space-x-4 mb-6">
                    <div className="w-12 h-12 bg-gradient-to-br from-indigo-500/10 to-indigo-600/20 rounded-xl flex items-center justify-center">
                      <Baby className="w-6 h-6 text-indigo-600" />
                    </div>
                    <h2 className="text-2xl font-bold text-primary uppercase">8. Children's Privacy</h2>
                  </div>
                  <div className="bg-gradient-to-r from-indigo-50/50 to-transparent rounded-2xl p-6">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Baby className="w-6 h-6 text-indigo-600" />
                      </div>
                      <p className="text-lg text-gray-700 leading-relaxed">
                        Our Website is not intended for children under the age of 13. We do not knowingly collect
                        personal information from children.
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Section 9: Updates to This Policy */}
              <motion.div id="updates" className="mb-12" variants={fadeInUp}>
                <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-xl border border-white/50 hover:shadow-2xl transition-shadow duration-500">
                  <div className="flex items-center space-x-4 mb-6">
                    <div className="w-12 h-12 bg-gradient-to-br from-pink-500/10 to-pink-600/20 rounded-xl flex items-center justify-center">
                      <RefreshCw className="w-6 h-6 text-pink-600" />
                    </div>
                    <h2 className="text-2xl font-bold text-primary uppercase">9. Updates to This Policy</h2>
                  </div>
                  <div className="bg-gradient-to-r from-pink-50/50 to-transparent rounded-2xl p-6">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-pink-100 rounded-xl flex items-center justify-center flex-shrink-0">
                        <RefreshCw className="w-6 h-6 text-pink-600" />
                      </div>
                      <p className="text-lg text-gray-700 leading-relaxed">
                        We may update this Privacy Policy periodically. Any changes will be posted with a revised
                        effective date.
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Section 10: Contact Us */}
              <motion.div id="contact" className="mb-12" variants={fadeInUp}>
                <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-xl border border-white/50 hover:shadow-2xl transition-shadow duration-500">
                  <div className="flex items-center space-x-4 mb-6">
                    <div className="w-12 h-12 bg-gradient-to-br from-primary/10 to-primary/20 rounded-xl flex items-center justify-center">
                      <Mail className="w-6 h-6 text-primary" />
                    </div>
                    <h2 className="text-2xl font-bold text-primary uppercase">10. Contact Us</h2>
                  </div>
                  <div className="bg-gradient-to-r from-primary/5 to-transparent rounded-2xl p-6">
                    <p className="text-lg text-gray-700 mb-6">
                      If you have any questions about this Privacy Policy, please contact us:
                    </p>
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                        <Mail className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <p className="text-lg text-gray-700">
                          Email:{" "}
                          <a
                            href="mailto:info@shefscloud.com"
                            className="text-primary hover:text-primary/80 underline transition-colors duration-300"
                          >
                            info@shefscloud.com
                          </a>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      <Footer />
    </>
  )
}

export default PrivacyPolicy
